import React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Stack,
} from "@mui/material";
import {
  useAddContractMutation,
  useAddDocumentContentMutation,
  useAddDocumentMutation,
  useAddPromptMutation,
  useGetDefaultPromptsQuery,
  useGetPromptsQuery,
  useGetSubscriptionInfoQuery,
} from "../../redux/slices/indexApiSlice";
import { IPrompt } from "../../types/prompt";
import { useTranslation } from "react-i18next";
import { ManualFileUploader } from "../../components/dialogs/InitiateDocumentsDialog";
import FilesToUploadList from "../../components/lists/FilesToUploadList";
import { DialogMode } from "../../types/dialogmode";
import { ContractStatus, IContract } from "../../types/contract";
import useAuth from "../../hooks/useAuth";
import { IDocument } from "../../types/document";
import { Assistant, Close as CloseIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { PromptsSelector } from "../main/onboarding/Onboarding";

type FirstTimeLoginDialogProps = {
  mode?: DialogMode;
  iconOnly?: boolean;
};

const FirstTimeLoginDialog: React.FC<FirstTimeLoginDialogProps> = ({
  mode,
  iconOnly,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [selectedPrompts, setSelectedPrompts] = useState<IPrompt[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const maxUploadFiles = 1;
  const [maxFilesReached, setMaxFilesReached] = useState<boolean>(false);
  const [allFiles, setAllFiles] = useState<File[]>([]);
  const [disableNewUploads, setDisableNewUploads] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<any>("");
  const [uploadsProgress, setUploadsProgress] = useState<
    {
      filename: string;
      progress: number;
    }[]
  >([]);
  const [isCreatingPrompts, setIsCreatingPrompts] = useState<boolean>(false);

  const { data: subscriptionInfo } = useGetSubscriptionInfoQuery();
  const { data: existingPrompts } = useGetPromptsQuery();
  const { data: defaultPrompts } = useGetDefaultPromptsQuery();

  const [
    addContract,
    {
      isLoading: addingContract,
      isSuccess: contractAdded,
      data: addedContract,
      reset: resetContractAdded,
      isError: errorWhileAddingContract,
      error: addedContractError,
    },
  ] = useAddContractMutation();

  const [
    addDocument,
    {
      isLoading: addingDocument,
      isSuccess: documentAdded,
      data: addedDocument,
      reset: resetDocumentAdd,
      isError: errorWhileAddingDocument,
      error: addedDocumentError,
    },
  ] = useAddDocumentMutation();

  const [
    uploadDocumentContent,
    {
      isLoading: isUploading,
      isSuccess: documentContentStored,
      reset: resetContent,
      isError: errorWhileUploading,
      error: uploadError,
    },
  ] = useAddDocumentContentMutation();

  const [addPrompt] = useAddPromptMutation();

  const steps = [
    t("firstTimeLoginDialog.step_uploadContract", {
      count: allFiles.length,
      max: maxUploadFiles,
    }),
    t("firstTimeLoginDialog.step_selectPrompts", {
      count: selectedPrompts.length,
      max: user?.account?.promptsLibraryLimit || 0,
    }),
    t("firstTimeLoginDialog.step_reviewAndImport"),
  ];

  const dialogWasOpened =
    localStorage.getItem("firstTimeLoginDialogOpened") === "true";

  useEffect(() => {
    if (user?.isFirstLogin) {
      if (dialogWasOpened) {
        return;
      }

      setOpen(true);
      // store the opened state in local storage
      localStorage.setItem("firstTimeLoginDialogOpened", "true");
    }
  }, [user]);

  useEffect(() => {
    if (
      (subscriptionInfo?.contractsRemaining &&
        subscriptionInfo.contractsRemaining - allFiles?.length < 0) ||
      (subscriptionInfo?.contractsRemaining === 0 && allFiles.length === 0)
    ) {
      setDisableNewUploads(true);
    } else {
      setDisableNewUploads(false);
    }
  }, [subscriptionInfo, allFiles]);

  useEffect(() => {
    if (allFiles?.length > maxUploadFiles) {
      setMaxFilesReached(true);
    } else {
      setMaxFilesReached(false);
    }
  }, [allFiles]);

  useEffect(() => {
    // console.log(filesToUpload);
    // console.log(documentsToImport);

    const newAllFiles = [...filesToUpload];

    if (newAllFiles?.length > maxUploadFiles) {
      const filesToRemove = newAllFiles.slice(maxUploadFiles);

      // remove these files from the filesToUpload or documentsToImport
      const newFilesToUpload = filesToUpload.filter(
        (f) => !filesToRemove.includes(f)
      );

      setFilesToUpload(newFilesToUpload);
    } else {
      setAllFiles(newAllFiles);
    }

    setAllFiles(newAllFiles);
  }, [filesToUpload]);

  const onTypeError = (error: any) => {
    setErrorType(error);
    console.log(error);
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      createPrompts().then(() => {
        uploadFiles();
      });
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleUploads = async (moreFiles: any) => {
    if (!moreFiles[0].origin || moreFiles[0].origin === "upload") {
      setFilesToUpload([...filesToUpload, ...moreFiles]);
    }
  };

  const deleteFileToUpload = (fileToDelete: File) => {
    // remove the files from filesToUpload
    const newFilesToUpload = filesToUpload.filter(
      (f) => f.name !== fileToDelete.name && f.size !== fileToDelete.size
    );

    setFilesToUpload([...newFilesToUpload]);
    // the file will be automatically removed from the allFiles by the useEffect
  };

  const createPrompts = async () => {
    if (!defaultPrompts || selectedPrompts.length === 0) {
      return;
    }

    setIsCreatingPrompts(true);

    const createdPrompts = await Promise.all(
      defaultPrompts
        .filter(
          (d) => existingPrompts?.find((e) => e.id === d.id) === undefined
        )
        .map((prompt) => {
          return addPrompt({
            title: prompt.title,
            body: prompt.body,
            author: user?.id,
            enabled: selectedPrompts.find((s) => s.id === prompt.id)
              ? true
              : false,
            hidden: false,
          }).unwrap();
        })
    )
      .then((results) => {
        return results;
      })
      .catch((error: any) => {
        console.error("Error creating prompts:", error);
        return [];
      });

    console.log("Created prompts:", createdPrompts);
    setIsCreatingPrompts(false);
  };

  const uploadFiles = async () => {
    const promises: PromiseLike<boolean>[] = [];
    filesToUpload?.forEach((f: File, i: number) => {
      promises.push(uploadFile(f, i));
    });

    await Promise.all(promises)
      .then(() => {
        // all done, close the dialog
      })
      .catch((error: any) => {
        console.error(error);
      });
  };

  const handleSuccess = () => {
    // navigate to the contract page
    if (addedContract) {
      navigate(`/contract/${addedContract.id}`);
    }

    onClose();
    setFilesToUpload([]);
    setAllFiles([]);
    setUploadsProgress([]);
  };

  const handleProgress = (
    filename: string,
    progress: number,
    index: number // pass the index in case there are documents with the same filename
  ) => {
    setUploadsProgress((prev: any) => {
      if (
        !prev.some((p: any) => p.filename === filename && p.index === index)
      ) {
        // initiate the progress
        return [...prev, { filename, progress, index }];
      } else {
        // update the progress
        const updatedProgress = prev.map((p: any) => {
          if (p.filename === filename && p.index === index) {
            p.progress = progress;
          }
          return p;
        });

        return updatedProgress;
      }
    });
  };

  const uploadFile = (f: File, index: number): PromiseLike<boolean> => {
    return new Promise((resolve, reject) => {
      handleProgress(f.name, 10, index);

      // create a contract for the file
      const newContract: any = {
        status: ContractStatus.PUBLISHED, // an imported contract is always published, drafts are new files that are created by the user or generated by AI
        name: f.name,
        ownerIds: [user.id],
        managerIds: [user.id],
      };

      addContract(newContract)
        .unwrap()
        .then((contract_payload) => {
          handleProgress(f.name, 40, index);

          // create a document for the file
          const newDocument: IDocument = {
            name: f.name,
            type: "contract",
            origin: "upload",
            location: "blobstorage",
            size: f.size,
            contractId: contract_payload.id,
          };

          addDocument(newDocument)
            .unwrap()
            .then((document_payload) => {
              handleProgress(f.name, 50, index);

              // upload the file content
              uploadLocalFileContentToAzureBlobStorage(
                f,
                document_payload,
                contract_payload
              )
                .then(() => {
                  handleProgress(f.name, 100, index);

                  resolve(true);
                })
                .catch((error: any) => {
                  reject(error);
                });
            })
            .catch((error: any) => {
              reject(error);
            });
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };

  const uploadLocalFileContentToAzureBlobStorage = async (
    fileToUpload: File,
    document: IDocument,
    contract: IContract
  ) => {
    if (document?.origin === "upload") {
      const formData = new FormData();
      formData.append("file", fileToUpload);
      await uploadDocumentContent({
        contractId: contract.id,
        documentId: document.id,
        file: formData,
      });
    }
  };

  const handleClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      // Prevent closing the dialog on backdrop click or escape key
      return;
    }

    onClose();
  };

  const onClose = () => {
    setOpen(false);
    setFilesToUpload([]);
    setAllFiles([]);
    setUploadsProgress([]);
    setSelectedPrompts([]);
    setActiveStep(0);
    setErrorType("");
    resetContractAdded();
    resetDocumentAdd();
    resetContent();
  };

  return (
    <>
      {mode === DialogMode.Add && (
        <>
          {iconOnly ? (
            <Tooltip title={t("Add vendors")}>
              <IconButton
                size="large"
                color="inherit"
                onClick={() => setOpen(true)}
              >
                <Assistant />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              onClick={() => setOpen(true)}
              startIcon={<Assistant />}
            >
              {t("Start onboarding")}
            </Button>
          )}
        </>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t("firstTimeLoginDialog.welcomeTitle", {
            name: user?.firstName || user?.displayName || user?.email || "",
          })}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => onClose()}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers sx={{ minHeight: "min(50vh,800px)" }}>
          {user?.account === undefined && <CircularProgress color="primary" />}

          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => {
              return (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          {activeStep === 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom color="textSecondary">
                {t("firstTimeLoginDialog.step_uploadContract_description")}
              </Typography>
              {/* <Alert severity="info" sx={{ mt: 2 }}>
              {t("firstTimeLoginDialog.step_uploadContract_description")}
            </Alert> */}
              {errorType ? (
                <Typography mt={2} mb={1} variant="subtitle1" color="error">
                  {errorType}
                </Typography>
              ) : (
                <></>
              )}
              {filesToUpload?.length > 0 ? (
                <FilesToUploadList
                  mode={DialogMode.Add}
                  filesWithProgress={allFiles
                    .filter((f) => f instanceof File)
                    .map((f: File, i: number) => {
                      const progress = uploadsProgress.find(
                        (p) => p.filename === f.name
                      );
                      return { file: f, progress: progress?.progress || 0 };
                    })}
                  handleDeleteFileToUpload={(index: number) =>
                    deleteFileToUpload(allFiles[index])
                  }
                  documents={[]}
                />
              ) : (
                <ManualFileUploader
                  onChange={handleUploads}
                  disabled={disableNewUploads}
                  onTypeError={onTypeError}
                  maxUploadFiles={maxUploadFiles}
                  maxFilesReached={filesToUpload.length >= 1}
                  disableNewUploads={disableNewUploads}
                />
              )}
            </Box>
          ) : activeStep === 1 ? (
            <PromptsSelector
              defaultPrompts={defaultPrompts}
              selectedPrompts={selectedPrompts}
              setSelectedPrompts={setSelectedPrompts}
            />
          ) : activeStep === 2 ? (
            <Stack sx={{ mt: 2 }} spacing={4}>
              <Alert severity="info" sx={{ my: 2 }}>
                {t("firstTimeLoginDialog.uploadedContractDescription")}
              </Alert>

              <div>
                <Typography variant="body1">{t("Contract")}:</Typography>

                <List sx={{ width: "100%" }} disablePadding dense>
                  {allFiles?.map((f: File, i: number) => (
                    <Typography fontWeight={700}>{f.name}</Typography>
                  ))}
                </List>
              </div>

              <div>
                <Typography sx={{ mt: 2, mb: 1 }}>{t("Prompts")}:</Typography>

                <Stack direction="row" gap={2} flexWrap="wrap">
                  {selectedPrompts?.map((p) => (
                    <Chip label={t(p.title || "")} sx={{ fontWeight: 700 }} />
                  ))}
                </Stack>
              </div>
            </Stack>
          ) : (
            <React.Fragment>
              <Typography sx={{ mt: 2, mb: 1 }}>
                Step {activeStep + 1}
              </Typography>
            </React.Fragment>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            {t("Back")}
          </Button>

          {activeStep < 2 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && allFiles.length === 0) ||
                (activeStep === 1 && selectedPrompts.length === 0)
              }
            >
              {t("Next")}
            </Button>
          )}

          {activeStep === 2 && (
            <>
              {uploadsProgress.length > 0 &&
              uploadsProgress.every((u) => u.progress === 100) ? (
                <Button
                  onClick={() => handleSuccess()}
                  variant="contained"
                  color="primary"
                >
                  {t("firstTimeLoginDialog.completed")}
                </Button>
              ) : (
                <Button
                  onClick={() => handleNext()}
                  variant="contained"
                  color="primary"
                  disabled={
                    (uploadsProgress.length > 0 &&
                      !uploadsProgress.every((u) => u.progress === 100)) ||
                    disableNewUploads ||
                    maxFilesReached ||
                    allFiles.length === 0 ||
                    addingContract ||
                    addingDocument ||
                    isUploading ||
                    selectedPrompts.length === 0 ||
                    isCreatingPrompts
                  }
                >
                  {allFiles.length === 1
                    ? t("Import1Contract")
                    : t("ImportXContracts", {
                        count: allFiles.length,
                      })}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FirstTimeLoginDialog;
